import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import { Tundish } from "../model/Tundish";
import { getTundishes } from "../repositories/TundishRepository";

type Props = {
  value: number | null;
  onChange: (value: number) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  highlight?: boolean;
  disableClearable?: boolean;
};

export const TundishSelector = ({
  value,
  onChange,
  onBlur = () => {},
  onFocus = () => {},
  disabled = false,
  onOpen = () => {},
  onClose = () => {},
  highlight = false,
  disableClearable = false,
}: Props) => {
  const [tundishes, setTundishes] = useState<Tundish[]>([]);
  const [selectedTundish, setSelectedTundish] = useState<Tundish | null>(null);
  const filteredTundishesRef = useRef<Tundish[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const loadData = async () => {
    try {
      setIsLoading(true);
      const resp = await getTundishes();
      if (resp.ok) {
        const rows = get(resp, "data.tables[0].rows", []) as Tundish[];
        setTundishes(rows);
      } else {
        setError(resp.message ?? "Error loading tundishes");
      }
    } catch (error) {
      setError("Error loading tundishes");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (tundishes.length > 0 && value) {
      const tundish = tundishes.find((t) => t.TundishId === value);
      setSelectedTundish(tundish || null);
    } else {
      setSelectedTundish(null);
    }
  }, [value, tundishes]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (filteredTundishesRef.current.length > 0) {
        const firstTundish = filteredTundishesRef.current[0];
        setSelectedTundish(firstTundish);
        if (firstTundish?.TundishId) {
          onChange(firstTundish.TundishId);
        }
        inputRef.current?.blur();
      }
    }
  };

  return (
    <>
      <Autocomplete
        id="tundish-selector"
        disableClearable={disableClearable}
        clearOnEscape={!disableClearable}
        options={tundishes || []}
        getOptionLabel={(option) => option.TundishName || ""}
        value={selectedTundish}
        onChange={(event, newValue) => {
          setSelectedTundish(newValue);
          if (newValue?.TundishId) {
            onChange(newValue.TundishId);
          }
        }}
        onOpen={onOpen}
        onClose={onClose}
        onInputChange={(event, value, reason) => {
          if (reason === "input") {
            filteredTundishesRef.current = tundishes.filter((tundish) =>
              tundish.TundishName?.toLowerCase().includes(value.toLowerCase()),
            );
          }
        }}
        isOptionEqualToValue={(option, value) =>
          option.TundishId === value.TundishId
        }
        disabled={disabled || isLoading}
        loading={isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            label="Tundish"
            variant="outlined"
            fullWidth
            sx={
              highlight
                ? {
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(249, 248, 113, 0.35)",
                    },
                  }
                : undefined
            }
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            onFocus={onFocus}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
              inputLabel: {
                sx: {
                  fontSize: "clamp(.7rem,.8rem,1rem)",
                },
              },
            }}
          />
        )}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
